<script setup>
/**
 * Canal de suporte (Chatwoot) embarcado na area logada.
 *
 *  - Carrega o SDK sob demanda (lazy) apenas quando ha usuario autenticado.
 *  - Busca a identidade assinada em GET /chat/identity (backend gera o HMAC).
 *  - Identifica o usuario no widget via $chatwoot.setUser (anti-impersonation).
 *  - Esconde a bolha padrao do Chatwoot e usa um LAUNCHER proprio (Appmax),
 *    que abre/fecha o chat via $chatwoot.toggle().
 *  - No logout, reseta a sessao do widget.
 *  - TRAVA TEMPORARIA: so habilita o suporte para contas @appmax.com.br,
 *    enquanto nao existe ambiente de homologacao para essa API/widget.
 *    Remover quando a validacao com o time interno terminar.
 */
import { ref, computed, watch, onMounted, onBeforeUnmount, inject } from 'vue'
import { storeToRefs } from 'pinia'
import axios from '@interceptor'
import { useAuthStore } from '@/stores/auth'
import { useChatSupportStore } from '@/stores/chatSupport'

const env = inject('env')
const authStore = useAuthStore()
const chatSupportStore = useChatSupportStore()
const { isAuthenticated } = storeToRefs(authStore)

const cfg = env.chatwoot

// Dominio liberado para o suporte durante a fase de validacao interna.
const ALLOWED_EMAIL_DOMAIN = /@appmax\.com\.br$/i

let sdkLoading = false
// Invalida respostas de /chat/identity ainda em voo a cada mudanca de auth:
// um fetch iniciado no login que so resolve depois do logout subia o SDK e
// chamava setUser com a identidade de quem ja saiu — e o reset() do logout
// nao cobria isso, porque naquele momento o SDK ainda nem tinha carregado.
let authGeneration = 0
const sdkReady = ref(false)
const chatOpen = ref(false)
// so vira true quando a identidade confirma um email @appmax.com.br
const allowed = ref(false)

// mostra o launcher Appmax so quando logado, com o SDK pronto e dominio liberado
const showLauncher = computed(
  () => cfg.enabled && isAuthenticated.value && sdkReady.value && allowed.value
)

// Publica a visibilidade do launcher: a bolha do changelog se desloca
// pra ficar ao lado dele em vez de empilhada por cima (ver Changelog.vue).
watch(
  showLauncher,
  visible => chatSupportStore.setLauncherVisible(visible),
  { immediate: true }
)

// Publica a largura REAL do launcher como CSS custom property. O changelog
// se desloca por ela (ver Changelog.vue) em vez de cravar um numero: assim
// uma troca de copy/i18n do label, do padding ou do icone nao volta a
// sobrepor as duas bolhas sem ninguem perceber.
const launcherEl = ref(null)
let launcherResizeObserver = null

function publishLauncherWidth(el) {
  document.documentElement.style.setProperty(
    '--am-chat-launcher-width',
    `${Math.round(el.getBoundingClientRect().width)}px`
  )
}

function clearLauncherWidth() {
  document.documentElement.style.removeProperty('--am-chat-launcher-width')
}

watch(
  launcherEl,
  el => {
    launcherResizeObserver?.disconnect()
    launcherResizeObserver = null

    if (!el) {
      clearLauncherWidth()
      return
    }

    // Mede uma vez ja; o observer cobre o resto (a fonte carrega async via
    // webfontloader e muda a largura do label depois da primeira pintura).
    publishLauncherWidth(el)

    if (typeof ResizeObserver === 'undefined') return
    launcherResizeObserver = new ResizeObserver(() => publishLauncherWidth(el))
    launcherResizeObserver.observe(el)
  },
  { flush: 'post' }
)

onBeforeUnmount(() => {
  launcherResizeObserver?.disconnect()
  clearLauncherWidth()
})

function isAllowedEmail(email) {
  return typeof email === 'string' && ALLOWED_EMAIL_DOMAIN.test(email.trim())
}

async function fetchIdentity() {
  try {
    const { data } = await axios.get(`${env.api.appbox.baseURL}/chat/identity`)
    return data?.data ?? null
  } catch (error) {
    // Falha de identidade nao deve quebrar a aplicacao; apenas loga.
    console.error('[ChatSupport] falha ao buscar identidade', error)
    return null
  }
}

function applyIdentity(identity) {
  if (!identity || !window.$chatwoot) return

  window.$chatwoot.setUser(identity.identifier, {
    name: identity.name,
    email: identity.email,
    identifier_hash: identity.identifier_hash
  })
}

function loadSdk(identity) {
  if (sdkLoading || sdkReady.value || !cfg.enabled || !cfg.websiteToken) return
  sdkLoading = true

  window.chatwootSettings = {
    position: 'right',
    locale: 'pt_BR',
    type: 'standard',
    // esconde a bolha nativa: usamos nosso proprio botao
    hideMessageBubble: true
  }

  const script = document.createElement('script')
  script.src = `${cfg.baseURL}/packs/js/sdk.js`
  script.defer = true
  script.async = true
  script.onload = () => {
    window.chatwootSDK.run({
      websiteToken: cfg.websiteToken,
      baseUrl: cfg.baseURL
    })
  }
  document.head.appendChild(script)

  window.addEventListener('chatwoot:ready', () => {
    sdkReady.value = true
    applyIdentity(identity)
  })
  // Eventos REAIS do SDK (nao existe 'chatwoot:toggle') — mantem nosso
  // estado de aberto/fechado sempre fiel ao estado real do widget, mesmo
  // quando o usuario fecha pelo X de dentro do proprio painel do chat
  // (sem isso, chatOpen so mudava quando NOS chamavamos toggle(), e
  // dessincronizava do estado real do SDK).
  window.addEventListener('chatwoot:opened', () => {
    chatOpen.value = true
  })
  window.addEventListener('chatwoot:closed', () => {
    chatOpen.value = false
  })
}

function toggleChat() {
  if (!window.$chatwoot) return
  // Sem argumento: o SDK decide com base no PROPRIO estado interno
  // (window.$chatwoot.isOpen), nunca no nosso `chatOpen` local — evita
  // dessincronia quando o widget foi fechado por fora do nosso botao.
  window.$chatwoot.toggle()
}

async function handleAuthChange(authenticated) {
  if (!cfg.enabled) return

  const generation = ++authGeneration

  if (authenticated) {
    const identity = await fetchIdentity()
    // Houve outro login/logout enquanto a identidade vinha: descarta.
    if (generation !== authGeneration) return

    allowed.value = isAllowedEmail(identity?.email)

    // Trava temporaria: sem identidade @appmax.com.br, nem carrega o SDK.
    if (!allowed.value) return

    if (!sdkReady.value) loadSdk(identity)
    else applyIdentity(identity)
  } else if (sdkReady.value && window.$chatwoot) {
    // Zera o estado local ja: showLauncher some assim que isAuthenticated
    // vira false, mas garantimos chatOpen limpo para a proxima sessao.
    chatOpen.value = false
    allowed.value = false
    try {
      window.$chatwoot.reset()
    } catch (error) {
      console.error('[ChatSupport] falha ao resetar widget no logout', error)
    }
  }
}

watch(isAuthenticated, handleAuthChange)
onMounted(() => handleAuthChange(isAuthenticated.value))
</script>

<template>
  <!-- Launcher proprio Appmax. O widget em si e' injetado no <body> pelo SDK. -->
  <button
    v-if="showLauncher"
    ref="launcherEl"
    class="am-chat-launcher"
    type="button"
    :aria-label="chatOpen ? 'Fechar suporte' : 'Abrir suporte'"
    @click="toggleChat"
  >
    <svg
      v-if="!chatOpen"
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8l-4 4V5a1 1 0 0 1 1-1Z"
        fill="currentColor"
      />
    </svg>
    <svg
      v-else
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
      />
    </svg>
    <span class="am-chat-launcher__label">Suporte</span>
  </button>
</template>

<style lang="scss" scoped>
.am-chat-launcher {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 2147483000; // acima do widget/app
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 56px;
    padding: 0 22px 0 18px;
    border: none;
    border-radius: 28px;
    background: linear-gradient(135deg, #9b6afa 0%, #7c4fe0 100%);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(123, 79, 224, 0.45);
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 26px rgba(123, 79, 224, 0.55);
    }

    &:active {
        transform: translateY(0);
    }

    &__label {
        line-height: 1;
    }
}

@media screen and (max-width: 480px) {
    .am-chat-launcher {
        right: 16px;
        bottom: 16px;
        padding: 0 18px;
        height: 52px;
    }
}
</style>
